import { useState } from 'react';
import { Link } from 'react-router-dom';
import { advancedMathQuestions } from './data/advancedMathQuestions';
import QuestionCard from '../algebra-practice/components/QuestionCard';
import ProgressBar from '../algebra-practice/components/ProgressBar';
import ResultsSummary from '../algebra-practice/components/ResultsSummary';
import { useScoreHistory } from '../../../hooks/useScoreHistory';

export default function AdvancedMathPracticePage() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const { saveScore } = useScoreHistory();

  const currentQuestion = advancedMathQuestions[currentQuestionIndex];
  const totalQuestions = advancedMathQuestions.length;
  const answeredCount = Object.keys(userAnswers).length;

  const handleAnswerSelect = (answer: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    // Calculate and save score
    let correctCount = 0;
    advancedMathQuestions.forEach(q => {
      if (userAnswers[q.id] === q.correctAnswer) correctCount++;
    });
    const percentage = Math.round((correctCount / totalQuestions) * 100);
    
    saveScore({
      examName: 'SAT Math: Advanced Math Practice',
      domain: 'Advanced Math',
      examType: 'math',
      score: correctCount,
      total: totalQuestions,
      percentage,
    });
    
    setShowResults(true);
  };

  const handleReview = () => {
    setReviewMode(true);
    setShowResults(false);
    setCurrentQuestionIndex(0);
  };

  const handleRetake = () => {
    setUserAnswers({});
    setShowResults(false);
    setReviewMode(false);
    setCurrentQuestionIndex(0);
  };

  const calculateResults = () => {
    let correct = 0;
    const subtopicStats: Record<string, { correct: number; total: number }> = {};

    advancedMathQuestions.forEach(q => {
      const userAnswer = userAnswers[q.id];
      const isCorrect = userAnswer === q.correctAnswer;
      
      if (isCorrect) correct++;

      if (!subtopicStats[q.subtopic]) {
        subtopicStats[q.subtopic] = { correct: 0, total: 0 };
      }
      subtopicStats[q.subtopic].total++;
      if (isCorrect) subtopicStats[q.subtopic].correct++;
    });

    return {
      correct,
      total: totalQuestions,
      percentage: Math.round((correct / totalQuestions) * 100),
      subtopicStats
    };
  };

  if (showResults) {
    const results = calculateResults();
    return (
      <ResultsSummary
        results={results}
        onReview={handleReview}
        onRetake={handleRetake}
        testTitle="SAT Math: Advanced Math Practice"
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Link 
                to="/sat-prep" 
                className="text-sm text-gray-600 hover:text-emerald-600 transition-colors flex items-center gap-2 mb-2"
              >
                <i className="ri-arrow-left-line"></i>
                Back to SAT Prep
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">SAT Math: Advanced Math Practice</h1>
              <p className="text-sm text-gray-600 mt-1">
                Quadratics, Polynomials, Exponentials, Rational Expressions & More
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-600">{answeredCount}/{totalQuestions}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Answered</div>
            </div>
          </div>
          <ProgressBar current={currentQuestionIndex + 1} total={totalQuestions} />
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <QuestionCard
          question={currentQuestion}
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
          selectedAnswer={userAnswers[currentQuestion.id]}
          onAnswerSelect={handleAnswerSelect}
          showSolution={reviewMode}
        />

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:border-emerald-500 hover:text-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-700 transition-all whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Previous
          </button>

          <div className="flex gap-2">
            {advancedMathQuestions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-10 h-10 rounded-lg font-medium transition-all ${
                  index === currentQuestionIndex
                    ? 'bg-emerald-600 text-white'
                    : userAnswers[advancedMathQuestions[index].id]
                    ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                    : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-emerald-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {currentQuestionIndex === totalQuestions - 1 ? (
            <button
              onClick={reviewMode ? () => setReviewMode(false) : handleSubmit}
              disabled={!reviewMode && answeredCount < totalQuestions}
              className="px-8 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 transition-all whitespace-nowrap"
            >
              {reviewMode ? 'Exit Review' : 'Submit Test'}
              <i className="ri-check-line ml-2"></i>
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-all whitespace-nowrap"
            >
              Next
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          )}
        </div>

        {/* Submit Reminder */}
        {!reviewMode && answeredCount === totalQuestions && currentQuestionIndex !== totalQuestions - 1 && (
          <div className="mt-6 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg text-center">
            <p className="text-emerald-800 font-medium">
              <i className="ri-checkbox-circle-line mr-2"></i>
              All questions answered! Navigate to question 20 to submit your test.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}